import React, { useState } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material'
import { Cart } from 'hooks/useCart'
import { formatToTitleCase } from 'utils/formatUtils'
import DiscountCodeForm from 'components/DiscountCodeForm'
import { useTranslation } from 'react-i18next'

type SummaryProps = {
  cart: Cart
  subTotal: string
  discountedAmount: string
  discountResult: { isValid?: boolean; rate?: number } | null
  tax: string
  assemblyFee: string
  totalPrice: string
  discountCode: string
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>
  validateDiscountCode: (code: string) => void
  removeDiscount: () => void
}

const CheckoutSummary: React.FC<SummaryProps> = ({
  cart,
  subTotal,
  discountedAmount,
  discountResult,
  tax,
  assemblyFee,
  totalPrice,
  discountCode,
  setDiscountCode,
  validateDiscountCode,
  removeDiscount
}) => {
  const { t } = useTranslation()
  const [isDiscountFormVisible, setIsDiscountFormVisible] = useState(false)

  return (
    <Box
      sx={{
        backgroundColor: '#F8F8F8',
        borderRadius: '12px',
        height: 'fit-content',
        padding: '10px',
        width: '350px',
        mb: 8
      }}
    >
      <List>
        <ListItem sx={{ paddingBottom: '32px' }}>
          <ListItemText
            primary={
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 500
                }}
              >
                {t('checkout.summary.title')}
              </Typography>
            }
          />
        </ListItem>

        {cart.items.map(item => (
          <Box key={item.productId} sx={{ mb: 2 }}>
            <ListItem>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Box sx={{ flex: 2 }}>
                      <Typography variant='h4'>{item.sku}</Typography>
                      {item.color !== 'Generic' && (
                        <Typography variant='body1'>{item.color}</Typography>
                      )}
                    </Box>
                    <Typography
                      variant='h4'
                      sx={{
                        flex: 1,
                        textAlign: 'center'
                      }}
                    >
                      *{item.quantity}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        flex: 1
                      }}
                    >
                      <Typography
                        variant='body1'
                        sx={{
                          fontSize: '20px',
                          fontWeight: 500
                        }}
                      >
                        ${item.price}
                      </Typography>
                    </Box>
                  </Box>
                }
                secondary={
                  item.isAssembled ? (
                    <Typography
                      variant='body1'
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {t('checkout.summary.assembled')}
                      {item.hingeDirection &&
                        `, ${formatToTitleCase(item.hingeDirection)}`}
                    </Typography>
                  ) : null
                }
              />
            </ListItem>
            <Divider sx={{ borderColor: '#E0E0E0' }} />
          </Box>
        ))}

        <ListItem>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 500
                  }}
                >
                  {t('checkout.summary.subtotal')}
                </Typography>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 500
                  }}
                >
                  ${subTotal}
                </Typography>
              </Box>
            }
          />
        </ListItem>

        {discountResult?.isValid && (
          <ListItem>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant='h3'
                    sx={{
                      fontWeight: 500
                    }}
                  >
                    {t('checkout.summary.discount')} ({discountResult.rate}%)
                  </Typography>
                  <Typography
                    variant='h3'
                    sx={{ fontWeight: 500, color: 'green' }}
                  >
                    -${discountedAmount}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        )}

        <ListItem>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 500
                  }}
                >
                  {t('checkout.summary.tax')}
                </Typography>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 500
                  }}
                >
                  ${tax}
                </Typography>
              </Box>
            }
          />
        </ListItem>

        <ListItem>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 500
                  }}
                >
                  {t('checkout.summary.assemblyFee')}
                </Typography>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 500
                  }}
                >
                  ${assemblyFee}
                </Typography>
              </Box>
            }
          />
        </ListItem>

        <ListItem sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <ListItemText>
            <Typography
              variant='h3'
              sx={{
                fontWeight: 500
              }}
            >
              {t('checkout.summary.total')}
            </Typography>
          </ListItemText>
          <Typography variant='h2'>${totalPrice}</Typography>
        </ListItem>
      </List>

      <Divider sx={{ my: 2, borderColor: '#E0E0E0' }} />

      <Box sx={{ px: 1.25 }}>
        <DiscountCodeForm
          discountCode={discountCode}
          setDiscountCode={setDiscountCode}
          isDiscountFormVisible={isDiscountFormVisible}
          setIsDiscountFormVisible={setIsDiscountFormVisible}
          validateDiscountCode={validateDiscountCode}
          discountResult={discountResult}
          onRemoveDiscount={() => {
            removeDiscount()
            setIsDiscountFormVisible(false)
          }}
        />
      </Box>
    </Box>
  )
}

export default CheckoutSummary
